import client from "services/ngage/api-client";
import DataPage from "types/DataPage";
import {Zone, ZoneData, ZoneDataAPI} from "types/Zone";
import {FeatureCollection} from "geojson";
import {queryString} from "services/string";
import deserializeZone from "services/ngage/deserializers/zone";
import {snakeKeys} from "../array";
import DataResource from "../../types/DataResource";
import {transformOpeningHoursForAPI} from "../opening-hours";

export type ZoneFilters = {
    search?: string | null
    mapped?: boolean
    page?: number
    per_page?: number
    site?: string
    floors?: number
    hidden?: boolean
}

/**
 * Get all zones (paginated)
 */
const all = async (parameters?: ZoneFilters) => {
    const query = queryString(parameters, {page: 1, per_page: 100})
    const response = await client.get<DataPage<Zone> | null>(`/zones?${query}`)

    if (response.data) {
        response.data.data = response.data.data.map(deserializeZone)
    }

    return response.data
}

/**
 * Get all zones that match the given filters (paginated)
 */
const filtered = async (filters: ZoneFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))
    const response = await client.get<DataResource<Zone> | null>('/zones', {params})

    if (response.data) {
        response.data.data = response.data.data.map(deserializeZone)
    }

    return response.data
}

/**
 * Get all zone areas as a GeoJSON feature collection
 */
const areas = async (excludedIds?: Array<number>) => {
    const zones = await all({mapped: true})
    const filteredZones = zones?.data.filter(zone => !excludedIds?.includes(zone.id)) || []

    const colors = ['#8e44ad', '#2980b9', '#27ae60', '#16a085', '#d35400', '#c0392b', '#f1c40f'];

    return {
        type: "FeatureCollection",
        features: filteredZones.map(zone => ({
            id: zone.id,
            type: "Feature",
            properties: {
                type: 'Zone',
                name: zone.name,
                description: zone.description,
                site: zone.site.name,
                color: colors[(zone.id || 0) % colors.length]
            },
            geometry: zone.area
        }))
    } as FeatureCollection
}

/**
 * Get all zones on a site (paginated)
 *
 * @param siteId
 * @param page
 * @param perPage
 */
const allOnSite = async (siteId: number, page: number = 1, perPage: number = 100) => {
    const query = queryString({site: siteId, page, per_page: perPage})
    const response = await client.get<DataPage<Zone> | null>(`/zones?${query}`)

    if (response.data) {
        response.data.data = response.data.data.map(deserializeZone)
    }

    return response.data
}

/**
 * Get a single zone
 *
 * @param {number} id
 */
const get = async (id: number) => {
    const response = await client.get<Zone>(`/zones/${id}`)
    return deserializeZone(response.data)
}

/**
 * Store a new zone
 *
 * @param data
 */
const store = async (data: ZoneData) => {
    const hours = data.hours ? transformOpeningHoursForAPI(data.hours) : null;
    const zoneData: ZoneDataAPI = {...data, hours};

    const response = await client.post<Zone>('/zones', zoneData)
    return deserializeZone(response.data)
}

/**
 * Update an existing zone
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: ZoneData) => {
    const hours = data.hours ? transformOpeningHoursForAPI(data.hours) : null;
    const zoneData: ZoneDataAPI = {...data, hours};

    const response = await client.put<Zone>(`/zones/${id}`, zoneData)
    return deserializeZone(response.data)
}

/**
 * Permanently delete a zone
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    const response = await client.delete<null>(`/zones/${id}`)
    return response.status === 204
}

const zones = {
    areas,
    all,
    allOnSite,
    filtered,
    get,
    store,
    update,
    destroy
}

export default zones;